// src/App.jsx
// ── KaiLux — Consolidated Route Map ──────────────────────────────────────────
// Merged from all build sessions (June 2026):
//
//   Pillar A  — Offline & Resilience   (OfflineBanner, OfflinePage)
//   Pillar B  — Admin Board            (AdminBoard, AnnouncementBanner, MaintenanceGate)
//   Phase D1  — Additional Documents   (AdditionalDocuments — sits between upload and /notes)
//   Pillar E  — Practice Workflow      (ClientIntake, IntakeRequests, OpeningBalanceCheck,
//                                        ComplianceCalendar, BatchProcessing)
//   Pillar F  — Management Accounts    (ManagementAccounts)
//   R8 scope  — Consolidations         (Consolidations)
//
// Public routes:  /login  /signup  /signoff/:token  /intake/:token  /offline
// Admin route:    /admin  (gated inside AdminBoard by role check)
// ─────────────────────────────────────────────────────────────────────────────

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './context/AuthContext'
import ProtectedRoute from './components/ProtectedRoute'
import HelpChat from './components/HelpChat'
import ErrorBoundary from './components/ErrorBoundary'
import ErrorToast from './components/ErrorToast'

// ── Pillar A ─────────────────────────────────────────────────────────────────
import OfflineBanner from './components/OfflineBanner'

// ── Pillar B ─────────────────────────────────────────────────────────────────
import { AnnouncementBanner } from './components/AnnouncementBanner'
import { MaintenanceGate }   from './components/MaintenanceGate'

// ── Core pages ───────────────────────────────────────────────────────────────
import Login         from './pages/Login'
import Signup        from './pages/Signup'
import ForgotPassword from './pages/ForgotPassword'
import ResetPassword  from './pages/ResetPassword'
import Onboarding    from './pages/Onboarding'
import PracticeVerificationPending from './pages/PracticeVerificationPending'
import IncompleteSignup from './pages/IncompleteSignup'
import VerifyEmailPending from './pages/VerifyEmailPending'
import Dashboard     from './pages/Dashboard'
import Clients       from './pages/Clients'
import AFSHistory    from './pages/AFSHistory'
import TrialBalance  from './pages/TrialBalance'
import BankStatement from './pages/BankStatement'
import BankStatementBalanceSheet from './pages/BankStatementBalanceSheet'
import Notes         from './pages/Notes'
import Review        from './pages/Review'
import ReviewFile    from './pages/ReviewFile'
import Statements    from './pages/Statements'
import Billing       from './pages/Billing'
import Settings      from './pages/Settings'
import Workpapers    from './pages/Workpapers'
import SignOff       from './pages/SignOff'
import AcceptInvite  from './pages/AcceptInvite'
import SsoLogin      from './pages/SsoLogin'

// ── Phase A2 ─────────────────────────────────────────────────────────────────
import OfflinePage from './pages/OfflinePage'

// ── Phase D1 — Additional Documents (optional step after upload, before Notes) ─
import AdditionalDocuments from './pages/AdditionalDocuments'
import TrustDetails from './pages/TrustDetails'

// ── Pillar E — Practice Workflow ─────────────────────────────────────────────
import ClientIntake        from './pages/ClientIntake'         // public, token-based
import VerifyAccountLink   from './pages/VerifyAccountLink'    // public, token-based
import IntakeRequests      from './pages/IntakeRequests'       // accountant-side management
import OpeningBalanceCheck from './pages/OpeningBalanceCheck'
import ComplianceCalendar  from './pages/ComplianceCalendar'
import BatchProcessing     from './pages/BatchProcessing'

// ── Pillar F — Management Accounts ──────────────────────────────────────────
import ManagementAccounts from './pages/ManagementAccounts'
import Audit from './pages/Audit'

// ── R8 scope — Consolidations ────────────────────────────────────────────────
import Consolidations from './pages/Consolidations'

import Icon from './components/Icon'
import { T } from './theme/theme'
// ─────────────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          @keyframes kailuxModalIn { from { opacity:0; transform:translateY(14px) scale(0.98); } to { opacity:1; transform:translateY(0) scale(1); } }
        `}</style>

        {/* Pillar B — maintenance gate wraps everything; skips for admins */}
        <MaintenanceGate>

          {/* Pillar A — global connectivity / AI circuit-breaker banner */}
          <OfflineBanner />

          {/* Pillar B — system announcement banner (set via admin board) */}
          <AnnouncementBanner />

          {/* Sep 2026 — global toast for backend 500s / network failures,
              with a shareable reference code (see kailux.js interceptors) */}
          <ErrorToast />

          <Routes>

            {/* ── Public ───────────────────────────────────────────────── */}
            <Route path="/"       element={<Navigate to="/login" replace />} />
            <Route path="/login"  element={<ErrorBoundary><Login /></ErrorBoundary>} />
            <Route path="/signup" element={<ErrorBoundary><Signup /></ErrorBoundary>} />
            <Route path="/forgot-password" element={<ErrorBoundary><ForgotPassword /></ErrorBoundary>} />
            <Route path="/reset-password"  element={<ErrorBoundary><ResetPassword /></ErrorBoundary>} />

            {/* Director sign-off — no login required.
                audience="client": the person here is an external director/client
                of the firm, not KaiLux firm staff — the boundary fallback should
                say "contact your accounting firm", not KaiLux support. */}
            <Route path="/signoff/:token" element={<ErrorBoundary audience="client"><SignOff /></ErrorBoundary>} />

            {/* Pillar E1 — client intake portal — no login required. Same
                audience="client" reasoning as /signoff/:token above. */}
            <Route path="/intake/:token" element={<ErrorBoundary audience="client"><ClientIntake /></ErrorBoundary>} />
            <Route path="/verify-account/:token" element={<ErrorBoundary><VerifyAccountLink /></ErrorBoundary>} />

            {/* Team invite acceptance — no login required (the token IS the credential) */}
            <Route path="/team/accept-invite" element={<ErrorBoundary><AcceptInvite /></ErrorBoundary>} />

            {/* Admin -> client-app SSO landing page — no login required
                (the short-lived token IS the credential). Reached only via
                the Admin Board's "Open Client App" link. */}
            <Route path="/sso" element={<ErrorBoundary><SsoLogin /></ErrorBoundary>} />

            {/* Phase A2 — offline shell page */}
            <Route path="/offline" element={<OfflinePage />} />

            {/* ── Protected — core AFS workflow ────────────────────────── */}
            <Route path="/onboarding"     element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            {/* Phase 9 — only reached when a firm's practice number conflicts with
                another active firm's at signup (practice_number_status = 'pending_verification') */}
            <Route path="/practice-verification" element={<ProtectedRoute><PracticeVerificationPending /></ProtectedRoute>} />
            {/* Orphaned-signup + interrupted-verification recovery (5 Sept 2026 —
                see kailux-fixes-log.md and kailux-master-plan.md item 16) */}
            <Route path="/signup-incomplete" element={<ProtectedRoute><IncompleteSignup /></ProtectedRoute>} />
            <Route path="/verify-email"      element={<ProtectedRoute><VerifyEmailPending /></ProtectedRoute>} />
            <Route path="/dashboard"      element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/clients"        element={<ProtectedRoute><Clients /></ProtectedRoute>} />
            <Route path="/afs-history"    element={<ProtectedRoute><AFSHistory /></ProtectedRoute>} />

            {/* Upload → Phase D1 optional step → Notes (order matters) */}
            <Route path="/trial-balance"          element={<ProtectedRoute><TrialBalance /></ProtectedRoute>} />
            <Route path="/bank-statement"         element={<ProtectedRoute><BankStatement /></ProtectedRoute>} />
            <Route path="/bank-statement/balance-sheet" element={<ProtectedRoute><BankStatementBalanceSheet /></ProtectedRoute>} />
            <Route path="/additional-documents"   element={<ProtectedRoute><AdditionalDocuments /></ProtectedRoute>} />
            <Route path="/trust-details"          element={<ProtectedRoute><TrustDetails /></ProtectedRoute>} />
            <Route path="/notes"                  element={<ProtectedRoute><Notes /></ProtectedRoute>} />

            <Route path="/review"         element={<ProtectedRoute><Review /></ProtectedRoute>} />
            <Route path="/review-file"    element={<ProtectedRoute><ReviewFile /></ProtectedRoute>} />
            <Route path="/statements"     element={<ProtectedRoute><Statements /></ProtectedRoute>} />
            <Route path="/workpapers"     element={<ProtectedRoute><Workpapers /></ProtectedRoute>} />

            {/* ── Protected — account & billing ────────────────────────── */}
            <Route path="/billing"        element={<ProtectedRoute><Billing /></ProtectedRoute>} />
            <Route path="/settings"       element={<ProtectedRoute><Settings /></ProtectedRoute>} />

            {/* ── R8 scope — Consolidations ────────────────────────────── */}
            <Route path="/consolidations" element={<ProtectedRoute><Consolidations /></ProtectedRoute>} />

            {/* ── Pillar E — Practice Workflow ─────────────────────────── */}
            <Route path="/intake-requests"       element={<ProtectedRoute><IntakeRequests /></ProtectedRoute>} />
            <Route path="/opening-balance-check" element={<ProtectedRoute><OpeningBalanceCheck /></ProtectedRoute>} />
            <Route path="/compliance"            element={<ProtectedRoute><ComplianceCalendar /></ProtectedRoute>} />
            <Route path="/batch"                 element={<ProtectedRoute><BatchProcessing /></ProtectedRoute>} />

            {/* ── Pillar F — Management Accounts ───────────────────────── */}
            <Route path="/audit"                 element={<ProtectedRoute><Audit /></ProtectedRoute>} />
            <Route path="/management-accounts"   element={<ProtectedRoute><ManagementAccounts /></ProtectedRoute>} />

            {/* Admin Board has moved to its own subdomain (admin.kailux.co.za,
                see src/AdminApp.jsx) — there is no /admin route here anymore,
                and nothing in this app links to one. */}

            {/* ── PayFast callbacks ─────────────────────────────────────── */}
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
            <Route path="/payment/cancel"  element={<ProtectedRoute><Billing /></ProtectedRoute>} />

            {/* ── 404 fallback ──────────────────────────────────────────── */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />

          </Routes>

          {/* Help chat — hidden on public/portal pages */}
          <AuthGate><HelpChat /></AuthGate>

        </MaintenanceGate>
      </BrowserRouter>
    </AuthProvider>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// AuthGate — suppress the help chat on pages that don't need it
// ─────────────────────────────────────────────────────────────────────────────
function AuthGate({ children }) {
  const path = window.location.pathname
  const publicPaths = ['/', '/login', '/signup', '/offline']
  if (publicPaths.includes(path)) return null
  if (path.startsWith('/signoff/')) return null
  if (path.startsWith('/intake/'))  return null
  return children
}

// ─────────────────────────────────────────────────────────────────────────────
// PaymentSuccess — inline since it's a one-liner screen
// ─────────────────────────────────────────────────────────────────────────────
function PaymentSuccess() {
  return (
    <div style={{
      minHeight: '100vh',
      background: T.bg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "'Outfit', sans-serif",
      color: T.tx,
    }}>
      <div style={{ textAlign: 'center', maxWidth: '400px' }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}><Icon name="check" /></div>
        <h1 style={{ fontSize: '28px', fontWeight: '800', marginBottom: '10px' }}>
          Payment successful!
        </h1>
        <p style={{ color: T.mt, marginBottom: '24px' }}>
          Your KaiLux subscription is now active.
        </p>
        <a
          href="/dashboard"
          style={{
            display: 'inline-block',
            padding: '12px 28px',
            background: T.prg,
            color: T.onpri,
            textDecoration: 'none',
            borderRadius: '10px',
            fontWeight: '700',
          }}
        >
          Open Dashboard →
        </a>
      </div>
    </div>
  )
}